/*
 * Copyright © 2024. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useState } from "react";
import InquiryToCustomer, { InquiryToCustomerStatus } from "@/src/types/Entities/InquiryToCustomer";
import { Box } from "@mui/material";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { FormProvider, useForm } from "@/src/provider/Form/FormProvider";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import Group from "@/src/components/Wizard/Group";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import InquiryToCustomerMediaObjects from "./InquiryToCustomerMediaObjects";
import * as messagesActions from "@/src/store/messages/actions";
import { useNavigate } from "react-router";
import { getCaseLink } from "@/src/services/productService";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/src/services/reactQuery/reactQueryService";

type InquiryToCustomerDecisionBoxProps = {
  inquiryToCustomer: InquiryToCustomer;
  product: AbstractCase;
  updateInquiryToCustomer: (data: Partial<InquiryToCustomer>) => Promise<InquiryToCustomer>;
};

export default function InquiryToCustomerDecisionBox({ inquiryToCustomer, product, updateInquiryToCustomer }: InquiryToCustomerDecisionBoxProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const initialValues = {
    inquiryDecisionAnswer: null,
  };

  const handleSubmit = async ({ values }: { values: Partial<InquiryToCustomer> }) => {
    setIsLoading(true);
    const payload: Partial<InquiryToCustomer> = {
      status: InquiryToCustomerStatus.STATUS_DONE,
      doneBy: "customer",
      inquiryDecisionAnswer: values.inquiryDecisionAnswer,
    };

    await updateInquiryToCustomer(payload);
    await queryClient.invalidateQueries({ queryKey: queryKeys.collections("/inquiry_to_customers") });
    setIsLoading(false);

    messagesActions.addMessage({
      type: "success",
      text: "Rückmeldung erfolgreich abgegeben",
    });
    navigate(getCaseLink(product) + "/anfrage/beantwortet");
  };

  return (
    <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
      <InquiryToCustomerDecisionForm inquiryToCustomer={inquiryToCustomer} isLoading={isLoading} />
    </FormProvider>
  );
}

type InquiryToCustomerDecisionFormProps = {
  inquiryToCustomer: InquiryToCustomer;
  isLoading: boolean;
};

const InquiryToCustomerDecisionForm = ({ inquiryToCustomer, isLoading }: InquiryToCustomerDecisionFormProps) => {
  const { handleSubmit, values } = useForm();

  return (
    <ContentBox headline={"Benötigte Informationen"}>
      <Box component={"form"} onSubmit={handleSubmit}>
        <InquiryToCustomerMediaObjects inquiryToCustomer={inquiryToCustomer} />
        <Box dangerouslySetInnerHTML={{ __html: inquiryToCustomer.inquiryDecisionQuestion ?? "" }} />
        <Group
          direction={"row"}
          type="radio"
          name="inquiryDecisionAnswer"
          question={""}
          options={[
            {
              labelText: "Ja",
              labelIcon: <CheckIcon />,
              labelIconActive: <CheckIcon active />,
              value: "yes",
            },
            {
              labelText: "Nein",
              labelIcon: <CrossIcon />,
              labelIconActive: <CrossIcon active />,
              value: "no",
            },
          ]}
        />
        <Box sx={{ marginTop: "1.5rem" }}>
          <ButtonLoading
            fullWidth
            variant={"contained"}
            color={"primary"}
            type={"submit"}
            isLoading={isLoading}
            disabled={values.inquiryDecisionAnswer === null}
          >
            Rückmeldung abschicken
          </ButtonLoading>
        </Box>
      </Box>
    </ContentBox>
  );
};
